import { useMemo, useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import {
  buildDayPanel,
  buildIntensityGrid,
  deriveBlocks,
  muscleName,
  catalogBySlug as buildCatalogBySlug,
  programMuscles,
  programToDraft,
  type Exercise,
  type Program as ProgramT,
  type UnitsPreference,
} from "@heraion/shared";

import { DayPanel } from "../IntensityGrid/DayPanel";
import { IntensityGridView } from "../IntensityGrid/IntensityGridView";
import type { UiTheme } from "../theme";
import { useKindLabels } from "../ProgramComposer/useKindLabels";
import { makePreviewStyles } from "./previewStyles";

// Read-only program preview (inline-expanded under a row's "View
// program ▾" toggle — collapsed by default, never auto-expanded).
// Lifted verbatim from apps/mobile in the Follow+Publish lift; the one
// platform seam (localized exercise-name resolution) is injected as
// resolveName, same adapter the composer uses. The colpo d'occhio: an
// aggregate muscles row + the shared session × week intensity grid,
// then the selected day's exercises grouped the way the program means
// them (supersets framed, week-ramps compressed to one line).
export function ProgramPreview({
  program,
  catalog,
  resolveName,
  unit,
  theme,
  dark,
}: {
  program: ProgramT;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  // Display unit for prescribed absolute loads (stored kg) — the twin of
  // PlanPreview's prop. Without it this preview showed kg/"KG" right next
  // to a correctly-localized plan preview in the same Library card.
  unit: UnitsPreference;
  theme: UiTheme;
  dark: boolean;
}) {
  const { t } = useTranslation();
  const kindLabels = useKindLabels(unit);
  const lang = useLocale();
  const styles = makePreviewStyles(theme);
  const [selected, setSelected] = useState(0);

  const catalogBySlug = useMemo(
    () => buildCatalogBySlug(catalog ?? []),
    [catalog],
  );

  // Derivation shared with PlanPreview + the Library tag-filter
  // (@heraion/shared muscleDerivation); sort-by-localized-name stays here.
  const muscles = useMemo(
    () =>
      programMuscles(program.sessions, catalogBySlug).sort((a, b) =>
        muscleName(a, lang).localeCompare(muscleName(b, lang)),
      ),
    [program, catalogBySlug, lang],
  );

  const grid = useMemo(
    () => buildIntensityGrid(program, lang),
    [program, lang],
  );
  // Seeded in the display unit so the summaries read in the same unit the
  // "KG"/"LB" label above them names. Read-only surface: nothing is saved
  // from this draft, so there is no matching build call to keep in step.
  const blocks = useMemo(
    () => deriveBlocks(programToDraft(program, unit, lang)),
    [program, unit, lang],
  );
  const entries = useMemo(
    () => buildDayPanel(blocks, selected, kindLabels),
    [blocks, selected, kindLabels],
  );

  return (
    <View style={styles.container}>
      {muscles.length > 0 ? (
        <View style={styles.musclesRow}>
          <Text style={styles.sectionLabel}>
            {t("mobile.preview.muscles", { defaultValue: "Muscles" })}
          </Text>
          <View style={styles.chipWrap}>
            {muscles.map((m) => (
              <View key={m} style={styles.muscleChip}>
                <Text style={styles.muscleChipText}>{muscleName(m, lang)}</Text>
              </View>
            ))}
          </View>
        </View>
      ) : null}

      <IntensityGridView
        grid={grid}
        selected={selected}
        onSelect={setSelected}
        dark={dark}
      />

      <DayPanel
        entries={entries}
        dayName={grid.days[selected]?.name ?? ""}
        cycleWeeks={program.cycleWeeks}
        dark={dark}
        resolveName={resolveName}
      />
    </View>
  );
}
